import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useBranding } from '@/lib/branding'
import { OnboardingHeader } from './OnboardingHeader'
import { OnboardingSidebar } from './OnboardingSidebar'
import { OnboardingFooter } from './OnboardingFooter'

/**
 * Public developer portal layout.
 * Structure:
 *   skip-to-content link
 *   sticky header (logo, display name, support link, operator login)
 *   ┌── sidebar (sticky, lg) ──┬── main content (prose) ──┐
 *   └─────────────────────────┴──────────────────────────┘
 *   footer
 *
 * No authentication is required — this layout is explicitly outside
 * the RequireAuth gate in router.tsx.
 */
export function OnboardingLayout() {
  const branding = useBranding()

  // Set the document title to reflect the developer portal rather
  // than the analyst console default. Restore the previous title on
  // unmount so navigating back into /(authed) pages doesn't carry
  // the portal title over.
  useEffect(() => {
    const previous = document.title
    document.title = `${branding.display_name || 'Antifraud'} — Developer Portal`
    return () => {
      document.title = previous
    }
  }, [branding.display_name])

  return (
    <div className="flex min-h-screen flex-col bg-bg text-fg">
      {/* WCAG 2.4.1 skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-bg focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow-lg focus:outline focus:outline-2 focus:outline-primary"
      >
        Skip to main content
      </a>

      <OnboardingHeader />

      <div className="flex flex-1 overflow-hidden">
        <OnboardingSidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 px-6 py-8 focus-visible:outline-none lg:px-12 lg:py-10"
            aria-label="Documentation content"
          >
            {/* Allow a wider container so the DocToc column can sit alongside
                the prose on xl viewports. DocPage handles its own internal
                flex layout (content + TOC). */}
            <div className="mx-auto w-full max-w-5xl">
              <Outlet />
            </div>
          </main>
          <OnboardingFooter />
        </div>
      </div>
    </div>
  )
}
